// Regras puras das telas de convite (Pedido de Entrega) — testáveis sem renderizar
// (mesmo padrão de paymentPlan.ts / destAddress.ts).

import { CRMPerson, DeliveryOrder, DELIVERY_ORDER_STATUS, DeliveryOrderAttendanceSummary } from '../../types/event';

// Logística própria (Viver Organizer) em [Shipping].[Providers].
export const INTERNAL_PROVIDER_ID = 1;

export interface InvitationRecipientForm {
  recipient_name: string;
  recipient_document: string;
  recipient_phone: string;
  recipient_email: string;
  crm_customer_id?: number;
  crm_comercial_partner_id?: number;
  ship_addr_street: string;
  ship_addr_num: string;
  ship_addr_compl: string;
  ship_addr_district: string;
  ship_addr_city: string;
  ship_addr_state: string;
  ship_addr_zip: string;
}

/**
 * Converte a pessoa do CRM em snapshot editável do convite. O vínculo de origem
 * (crm_customer_id OU crm_comercial_partner_id) é mutuamente exclusivo, decidido
 * pelo kind — nunca os dois.
 */
export function guestToInvitationForm(person: CRMPerson): InvitationRecipientForm {
  return {
    recipient_name: person.name || '',
    recipient_document: person.document || '',
    recipient_phone: person.cell_phone || person.phone || '',
    recipient_email: person.email || '',
    crm_customer_id: person.kind === 'customer' ? person.id : undefined,
    crm_comercial_partner_id: person.kind === 'commercial_partner' ? person.id : undefined,
    ship_addr_street: person.addr_street || '',
    ship_addr_num: person.addr_num || '',
    ship_addr_compl: person.addr_compl || '',
    ship_addr_district: person.addr_district || '',
    ship_addr_city: person.addr_city || '',
    ship_addr_state: person.addr_state || '',
    ship_addr_zip: (person.addr_zip || '').replace(/\D/g, ''),
  };
}

/**
 * Convite → formulário de edição do convidado. Espelho de guestToInvitationForm, mas
 * partindo do snapshot já gravado no convite (e não da pessoa do CRM): é o que abre o
 * modo de edição da tela de detalhe.
 */
export function orderToRecipientForm(d: DeliveryOrder): InvitationRecipientForm {
  return {
    recipient_name: d.recipient_name || '',
    recipient_document: d.recipient_document || '',
    recipient_phone: d.recipient_phone || '',
    recipient_email: d.recipient_email || '',
    crm_customer_id: d.crm_customer_id,
    crm_comercial_partner_id: d.crm_comercial_partner_id,
    ship_addr_street: d.ship_addr_street || '',
    ship_addr_num: d.ship_addr_num || '',
    ship_addr_compl: d.ship_addr_compl || '',
    ship_addr_district: d.ship_addr_district || '',
    ship_addr_city: d.ship_addr_city || '',
    ship_addr_state: d.ship_addr_state || '',
    ship_addr_zip: d.ship_addr_zip || '',
  };
}

/**
 * Formulário → payload do PATCH. Normaliza o que o backend/SEFAZ esperam: documento e
 * CEP só com dígitos, UF em maiúsculas, textos aparados. Os vínculos com o CRM não vão
 * no payload — a origem do convidado não muda numa edição de dados.
 */
export function recipientFormToPatch(form: InvitationRecipientForm): Record<string, string> {
  return {
    recipient_name: form.recipient_name.trim(),
    recipient_document: form.recipient_document.replace(/\D/g, ''),
    recipient_phone: form.recipient_phone.trim(),
    recipient_email: form.recipient_email.trim(),
    ship_addr_street: form.ship_addr_street.trim(),
    ship_addr_num: form.ship_addr_num.trim(),
    ship_addr_compl: form.ship_addr_compl.trim(),
    ship_addr_district: form.ship_addr_district.trim(),
    ship_addr_city: form.ship_addr_city.trim(),
    ship_addr_state: form.ship_addr_state.trim().toUpperCase(),
    ship_addr_zip: form.ship_addr_zip.replace(/\D/g, ''),
  };
}

/**
 * Campos que a emissão da NF de bonificação exige — mesma lista validada no backend
 * (validateInvitationDispatchAddress + required do CreateBlingInvoiceRequest). Devolve
 * os rótulos que faltam, para a tela avisar ANTES de o usuário tentar despachar.
 */
export function missingInvoiceFields(d: Pick<DeliveryOrder,
  'recipient_document' | 'ship_addr_street' | 'ship_addr_district' | 'ship_addr_city' | 'ship_addr_state' | 'ship_addr_zip'>): string[] {
  const missing: string[] = [];
  const check = (v: string | undefined, label: string) => {
    if (!v || !v.trim()) missing.push(label);
  };
  check(d.recipient_document, 'CPF/CNPJ');
  check(d.ship_addr_street, 'logradouro');
  check(d.ship_addr_district, 'bairro');
  check(d.ship_addr_city, 'cidade');
  check(d.ship_addr_state, 'UF');
  check(d.ship_addr_zip, 'CEP');
  return missing;
}

/** Variante do Badge por status do Pedido de Entrega. */
export function statusBadgeVariant(statusId: number): 'success' | 'info' | 'warning' | 'neutral' | 'danger' {
  switch (statusId) {
    case DELIVERY_ORDER_STATUS.PENDING:
      return 'warning';
    case DELIVERY_ORDER_STATUS.SHIPPED:
      return 'info';
    case DELIVERY_ORDER_STATUS.DELIVERED:
      return 'success';
    case DELIVERY_ORDER_STATUS.CANCELED:
      return 'danger';
    default:
      return 'neutral';
  }
}

/**
 * NF de bonificação (CFOP 5.949/6.949): exige transportadora selecionada (a cotação
 * escolhida carimba o provider no convite), convite não cancelado e sem NF vinculada.
 * Desde 2026-08 a logística própria TAMBÉM emite NF — pela tela de emissão, onde se
 * escolhe o veículo/placa da frota (decisão de negócio; antes era bloqueada).
 */
export function canEmitInvoice(d: Pick<DeliveryOrder, 'provider_id' | 'status_id' | 'invoice_id'>): boolean {
  if (d.invoice_id) return false; // já emitida/vinculada
  if (d.status_id === DELIVERY_ORDER_STATUS.CANCELED) return false;
  return d.provider_id !== undefined && d.provider_id !== null;
}

/** Envio pela logística própria (ou ainda sem transportadora definida) entra em rota. */
export function isOwnLogistics(d: Pick<DeliveryOrder, 'provider_id'>): boolean {
  return d.provider_id === undefined || d.provider_id === null || d.provider_id === INTERNAL_PROVIDER_ID;
}

// Integração direta Correios (CWS) em [Shipping].[Providers].
export const CORREIOS_PROVIDER_ID = 10;

// [Financial].[Invoice].StatusId — 2 = Cancelada.
export const INVOICE_STATUS_CANCELED = 2;

/**
 * Desvincular a NF do convite só faz sentido (e só é aceito pelo backend) quando a nota
 * vinculada foi CANCELADA: é o que destrava o convite para uma nova emissão depois de uma
 * nota que precisou ser cancelada — por exemplo, uma rejeição dos Correios. Desvincular
 * uma NF válida deixaria a nota órfã.
 */
export function canUnlinkInvoice(d: Pick<DeliveryOrder, 'invoice_id' | 'invoice_status_id'>): boolean {
  return !!d.invoice_id && d.invoice_status_id === INVOICE_STATUS_CANCELED;
}

/**
 * Despacho Correios em 1 clique (emite NF de bonificação + pré-postagem): só com a
 * cotação dos Correios selecionada (provider + serviço, ex. SEDEX) e convite ainda
 * Pendente — depois de integrado o convite vira Enviado e resta imprimir as etiquetas.
 */
export function canDispatchCorreios(d: Pick<DeliveryOrder, 'provider_id' | 'provider_service_id' | 'status_id'>): boolean {
  if (d.status_id !== DELIVERY_ORDER_STATUS.PENDING) return false;
  return d.provider_id === CORREIOS_PROVIDER_ID
    && d.provider_service_id !== undefined && d.provider_service_id !== null;
}

/**
 * PDF único de impressão (etiqueta da NF + etiquetas dos Correios): precisa da NF
 * vinculada, do envio pelos Correios e de ao menos um objeto postado (pré-postagem OK).
 */
export function canPrintInvitationLabels(
  d: Pick<DeliveryOrder, 'provider_id' | 'invoice_id'>,
  correiosObjectsCount: number,
): boolean {
  return !!d.invoice_id && d.provider_id === CORREIOS_PROVIDER_ID && correiosObjectsCount > 0;
}

// ── Comparecimento ao evento ────────────────────────────────────────────────
// Não confundir com o status_id: aquele é a etapa LOGÍSTICA do convite (o convite chega na
// casa da convidada dias antes); comparecimento é se ela apareceu. Três estados, de propósito:
// "não informado" (ninguém conferiu a lista ainda) é diferente de "não compareceu".

export type AttendanceState = 'attended' | 'not_attended' | 'unknown';

/** Estado de comparecimento de um convite. */
export function attendanceState(d: Pick<DeliveryOrder, 'attended'>): AttendanceState {
  if (d.attended === true) return 'attended';
  if (d.attended === false) return 'not_attended';
  return 'unknown';
}

/** Rótulo pt-BR do estado de comparecimento. */
export function attendanceLabel(state: AttendanceState): string {
  switch (state) {
    case 'attended':
      return 'Compareceu';
    case 'not_attended':
      return 'Não compareceu';
    default:
      return 'Não informado';
  }
}

/**
 * Próximo valor ao clicar num dos dois botões da listagem. Clicar no estado já ativo
 * desmarca (volta a "não informado") — é o que permite corrigir uma marcação errada sem
 * abrir o detalhe do convite.
 */
export function nextAttendance(current: boolean | null | undefined, clicked: boolean): boolean | null {
  return current === clicked ? null : clicked;
}

/** Chave do contador correspondente a um valor de comparecimento. */
const summaryKey = (v: boolean | null | undefined): keyof DeliveryOrderAttendanceSummary =>
  v === true ? 'attended' : v === false ? 'not_attended' : 'unknown';

/**
 * Reconta os totais depois de uma marcação, sem recarregar a listagem: tira 1 do balde antigo
 * e põe 1 no novo. Os contadores são da listagem INTEIRA, então não dá para derivá-los da
 * página corrente.
 */
export function recountSummary(
  current: DeliveryOrderAttendanceSummary,
  from: boolean | null | undefined,
  to: boolean | null | undefined,
): DeliveryOrderAttendanceSummary {
  const fromKey = summaryKey(from);
  const toKey = summaryKey(to);
  if (fromKey === toKey) return current;
  return {
    ...current,
    [fromKey]: Math.max(0, current[fromKey] - 1),
    [toKey]: current[toKey] + 1,
  };
}
